import MapInstance from "@components/Map/mapInstance";
import PreviewMissionMarkers from "@components/Map/previewMarkers";
import type { Mission } from "@components/Map/type";
import { PreviewType } from "@components/Map/type";
import { Grid, Typography } from "@mui/material";
import { useSelector } from "@tanstack/react-store";
import { DeliveryStatus, DeliveryType } from "@types_def/models/delivery.types";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { DeliveryFormType } from "../types";

const LatLngSchema = z.object({
  lat: z
    .number()
    .finite()
    .refine((n) => n !== 0),
  lng: z
    .number()
    .finite()
    .refine((n) => n !== 0),
});

type Props = {
  form: DeliveryFormType;
  type: DeliveryType;
  previewType?: PreviewType;
};

function DeliveryMap({ form, previewType = PreviewType.AB, type }: Props) {
  const { t } = useTranslation();

  const fromAddress = useSelector(form.store, (s) => s.values.step1?.fromAddress);
  const isTwoWay = type === DeliveryType.TWO_WAY_DELIVERY;

  const toAddress = useSelector(form.store, (s) =>
    isTwoWay && previewType === PreviewType.BC
      ? s.values.step2?.toReturnFromAddress
      : s.values.step1?.toAddress
  );
  const toReturnToAddress = useSelector(form.store, (s) => s.values.step2?.toReturnToAddress);

  const originResult = LatLngSchema.safeParse({
    lat: fromAddress?.latitude,
    lng: fromAddress?.longitude,
  });

  const destinationResult = LatLngSchema.safeParse({
    lat: toAddress?.latitude,
    lng: toAddress?.longitude,
  });

  const returnResult = LatLngSchema.safeParse({
    lat: toReturnToAddress?.latitude,
    lng: toReturnToAddress?.longitude,
  });

  const hasValidOrigin = originResult.success;
  const hasValidDestination = destinationResult.success;
  const hasValidReturn = returnResult.success;

  const canShowAB = hasValidOrigin && hasValidDestination;
  const canShowBC = hasValidDestination && hasValidReturn;
  const canShowABC = hasValidOrigin && hasValidDestination && hasValidReturn;

  const showMap =
    (previewType === PreviewType.AB && canShowAB) ||
    (previewType === PreviewType.BC && canShowBC) ||
    (previewType === PreviewType.ABC && canShowABC);

  const hasMarkersForAB = hasValidOrigin && hasValidDestination;
  const hasMarkersForBC = hasValidDestination && hasValidReturn;
  const hasMarkersForABC = hasValidOrigin && hasValidDestination && hasValidReturn;

  const shouldRenderMarkers =
    showMap &&
    ((previewType === PreviewType.AB && hasMarkersForAB) ||
      (previewType === PreviewType.BC && hasMarkersForBC) ||
      (previewType === PreviewType.ABC && hasMarkersForABC));

  const buildMission = (): Mission => ({
    status: DeliveryStatus.REQUESTED,
    type,
    origin: { lat: fromAddress.latitude, lng: fromAddress.longitude },
    destination: { lat: toAddress.latitude, lng: toAddress.longitude },
    toReturn: hasValidReturn
      ? { lat: toReturnToAddress.latitude, lng: toReturnToAddress.longitude }
      : { lat: 0, lng: 0 },
    restitution: isTwoWay ? (toReturnToAddress?.address ?? "") : "",
    originCity: fromAddress?.city,
    destinationCity: toAddress?.city,
    depart: fromAddress?.address,
    arrive: toAddress?.address,
    color: "green",
    id: "0",
  });

  return (
    <Grid container direction="row" spacing={2} paddingBottom={2} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%", minHeight: "40vh" }}>
        {showMap ? (
          <MapInstance>
            {shouldRenderMarkers && (
              <PreviewMissionMarkers mission={buildMission()} previewType={previewType} />
            )}
          </MapInstance>
        ) : (
          <Grid
            container
            width="100%"
            height="100%"
            justifyContent="center"
            alignContent="center"
            padding={5}
          >
            <Typography fontSize="1.1rem" margin={10}>
              {t("delivery.form.map-no-address")}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default DeliveryMap;
